import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, Send, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";

const MILA_AVATAR =
  "https://static.prod-images.emergentagent.com/jobs/6574159f-288e-4b5c-b16a-0836ebfc6103/images/d30f981de59445037e0701a7e7b07fd1ce3eb59e0d1dfb009169d36ea17a1f68.jpeg";

// ── Conversation flow ──────────────────────────────────────────────────────
const FLOW = {
  welcome: {
    msgs: ["Hey! I'm Mila, Hylas Companion's in-house recruiter.", "What type of driving are you looking for?"],
    type: "options",
    options: [
      { label: "OTR", next: "otr", meta: { positionType: "OTR" } },
      { label: "Regional / Local", next: "regional", meta: { positionType: "Regional" } },
      { label: "Lease to Own", next: "lease", meta: { positionType: "Lease to Own" } },
      { label: "Not sure yet", next: "not_sure" },
    ],
  },
  otr: {
    msgs: [
      "OTR is our most popular seat. Company drivers earn 85¢+ CPM — that's roughly $110k–$130k/year at 2,500–3,000 miles/week.",
      "Team seats pay $1/mile. No held first check, zero escrow. Sound like the range you're after?",
    ],
    type: "options",
    options: [
      { label: "Yes, that works!", next: "fleet" },
      { label: "Tell me about Lease", next: "lease", meta: { positionType: "Lease to Own" } },
      { label: "What about Regional?", next: "regional", meta: { positionType: "Regional" } },
    ],
  },
  regional: {
    msgs: [
      "Regional and dedicated lanes get you more home time. We run strong freight in the Midwest, Southeast, and the Texas Triangle.",
      "Are you thinking home daily or a few nights out?",
    ],
    type: "options",
    options: [
      { label: "Home daily", next: "fleet", meta: { positionType: "Local / Daily Home" } },
      { label: "Few nights out", next: "fleet", meta: { positionType: "Regional" } },
    ],
  },
  lease: {
    msgs: [
      "Lease to Own runs at 88% gross freight revenue. Most drivers net $2,000–$3,500+/week.",
      "At the end of the term you own the truck — no balloon payment. We also have a Rent Program if you'd rather have flexibility.",
    ],
    type: "options",
    options: [
      { label: "Lease sounds good", next: "fleet" },
      { label: "Tell me about Rent", next: "rent", meta: { positionType: "Rent Program" } },
    ],
  },
  rent: {
    msgs: [
      "The Rent Program is also 88% revenue share — same upside — but no ownership path and more flexible terms.",
      "Great if you want the owner-operator income without a long-term commitment. Sound interesting?",
    ],
    type: "options",
    options: [
      { label: "Yes, fits me", next: "fleet" },
      { label: "Actually, Lease is better", next: "fleet", meta: { positionType: "Lease to Own" } },
    ],
  },
  not_sure: {
    msgs: ["No worries — that's what I'm here for!", "What matters most to you right now?"],
    type: "options",
    options: [
      { label: "Maximize pay", next: "otr", meta: { positionType: "OTR" } },
      { label: "More home time", next: "regional", meta: { positionType: "Regional" } },
      { label: "Own my truck", next: "lease", meta: { positionType: "Lease to Own" } },
    ],
  },
  fleet: {
    msgs: [
      "We run new iron only — Peterbilt 579, Kenworth T680, Freightliner Cascadia, Volvo VNL. All 2020–2026 models.",
      "Every truck comes with an APU, fridge, and a 2,000W inverter — standard. Do you have a preferred make?",
    ],
    type: "options",
    options: [
      { label: "Peterbilt", next: "policies", meta: { truckPref: "Peterbilt 579" } },
      { label: "Kenworth", next: "policies", meta: { truckPref: "Kenworth T680" } },
      { label: "Freightliner", next: "policies", meta: { truckPref: "Freightliner Cascadia" } },
      { label: "Volvo", next: "policies", meta: { truckPref: "Volvo VNL" } },
      { label: "No preference", next: "policies", meta: { truckPref: "No preference" } },
    ],
  },
  policies: {
    msgs: [
      "A few things drivers always ask us: no inside cameras · first check never held · SAP drivers welcome · female drivers treated exactly the same · no escrow on company seats.",
      "Any of that important to know?",
    ],
    type: "options",
    options: [
      { label: "All of it matters!", next: "cdl" },
      { label: "Good to know, keep going", next: "cdl" },
    ],
  },
  cdl: {
    msgs: ["Almost there! Do you have your CDL-A?"],
    type: "options",
    options: [
      { label: "Yes, I have it", next: "lead_intro", meta: { hasCDL: "Yes" } },
      { label: "Getting it now", next: "trainee", meta: { hasCDL: "In progress" } },
      { label: "Not yet", next: "trainee", meta: { hasCDL: "No" } },
    ],
  },
  trainee: {
    msgs: [
      "No worries — our Trainee Program is open to new CDL-A holders.",
      "You'll ride with a seasoned driver first, then move to your own solo seat. No prior OTR experience needed. Sound like a fit?",
    ],
    type: "options",
    options: [
      { label: "Yes, that works!", next: "lead_intro", meta: { positionType: "Trainee Program" } },
      { label: "How much does trainee pay?", next: "lead_intro", meta: { positionType: "Trainee Program" } },
    ],
  },
  lead_intro: {
    msgs: ["I think we've got a great fit for you!", "Would you like one of our recruiters to call you? What's your first name?"],
    type: "input",
    inputKey: "name",
    inputPlaceholder: "Your first name...",
    next: "lead_phone",
  },
  lead_phone: {
    msgs: [], // dynamic — set via getDynamicMsgs
    type: "input",
    inputKey: "phone",
    inputPlaceholder: "Best phone number...",
    next: "done",
  },
  done: {
    msgs: [], // dynamic
    type: "done",
  },
};

function getDynamicMsgs(stepId, ctx) {
  if (stepId === "lead_phone") {
    return [`Nice to meet you, ${ctx.name || "there"}! What's the best number to reach you?`];
  }
  if (stepId === "done") {
    return [
      `You're all set, ${ctx.name || "there"}! I've passed your info to the team.`,
      "Expect a call within 1 business day. You can also apply directly — it takes under 3 minutes.",
    ];
  }
  return FLOW[stepId]?.msgs || [];
}

// ── Typing bubble ─────────────────────────────────────────────────────────
function TypingBubble() {
  return (
    <div className="flex items-end gap-2 mb-3">
      <img src={MILA_AVATAR} alt="Mila" className="w-7 h-7 rounded-full object-cover shrink-0" />
      <div className="bg-white border border-gray-100 shadow-sm px-4 py-3 max-w-[80%]">
        <div className="flex items-center gap-1">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-gray-400"
              style={{ animation: `milaDot 1.2s ${i * 0.2}s infinite` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Message bubble ────────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isMila = msg.from === "mila";
  return (
    <div className={`flex items-end gap-2 mb-3 ${isMila ? "" : "flex-row-reverse"}`}>
      {isMila && (
        <img src={MILA_AVATAR} alt="Mila" className="w-7 h-7 rounded-full object-cover shrink-0 mb-0.5" />
      )}
      <div
        className={`px-4 py-2.5 max-w-[80%] text-sm leading-relaxed ${
          isMila
            ? "bg-white border border-gray-100 shadow-sm text-gray-800"
            : "bg-[#0A0A0A] text-white"
        }`}
      >
        {msg.text}
      </div>
    </div>
  );
}

// ── Main widget ───────────────────────────────────────────────────────────
export default function MilaChat() {
  const [open, setOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState("welcome");
  const [context, setContext] = useState({ positionType: "OTR", truckPref: "", name: "", phone: "", hasCDL: "" });
  const [typing, setTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);

  // Focus input when it appears
  const currentNode = FLOW[step];
  useEffect(() => {
    if (open && currentNode?.type === "input") {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [step, open, currentNode?.type]);

  const addMsg = useCallback((from, text) => {
    setMessages((prev) => [...prev, { id: `${from}-${Date.now()}-${Math.random()}`, from, text }]);
  }, []);

  const runStep = useCallback((stepId, ctx) => {
    setStep(stepId);
    const msgs = getDynamicMsgs(stepId, ctx);
    if (!msgs.length) return;
    setTyping(true);
    const delay = Math.min(1200, 600 + msgs.length * 200);
    setTimeout(() => {
      setTyping(false);
      msgs.forEach((text, i) => {
        setTimeout(() => addMsg("mila", text), i * 250);
      });
    }, delay);
  }, [addMsg]);

  // Start conversation when chat first opens
  useEffect(() => {
    if (open && !started) {
      setStarted(true);
      setHasUnread(false);
      runStep("welcome", context);
    }
    if (open) setHasUnread(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOption = (option) => {
    addMsg("driver", option.label);
    const newCtx = { ...context, ...(option.meta || {}) };
    setContext(newCtx);
    setTimeout(() => runStep(option.next, newCtx), 300);
  };

  const validateInput = (key, val) => {
    if (key === "name") return val.trim().length >= 2 ? "" : "Please enter your name.";
    if (key === "phone") {
      const digits = val.replace(/\D/g, "");
      return digits.length >= 10 ? "" : "Please enter a valid phone number.";
    }
    return "";
  };

  const handleInputSubmit = async () => {
    const node = FLOW[step];
    if (!node || node.type !== "input") return;
    const err = validateInput(node.inputKey, inputValue);
    if (err) { setInputError(err); return; }
    setInputError("");
    const val = inputValue.trim();
    setInputValue("");
    addMsg("driver", val);
    const newCtx = { ...context, [node.inputKey]: val };
    setContext(newCtx);

    if (node.next === "done") {
      // Save lead
      setSubmitting(true);
      try {
        await api.submitChatLead({
          name: newCtx.name,
          phone: newCtx.phone,
          position_interest: newCtx.positionType || "OTR",
          truck_preference: newCtx.truckPref || "",
          has_cdl: newCtx.hasCDL || "",
        });
      } catch (e) {
        console.error("Chat lead save failed:", e);
      } finally {
        setSubmitting(false);
      }
      setTimeout(() => runStep("done", newCtx), 300);
    } else {
      setTimeout(() => runStep(node.next, newCtx), 300);
    }
  };

  const isOptions = !typing && currentNode?.type === "options";
  const isInput = !typing && currentNode?.type === "input";
  const isDone = currentNode?.type === "done" && messages.some((m) => m.from === "mila" && m.text.includes("all set"));

  return (
    <>
      <style>{`
        @keyframes milaDot {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
        @keyframes milaPulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.15); opacity: 0.7; }
        }
      `}</style>

      {/* ── Collapsed button ── */}
      {!open && (
        <button
          data-testid="mila-open-btn"
          onClick={() => setOpen(true)}
          className="fixed bottom-[76px] right-4 md:bottom-6 md:right-6 z-50 flex flex-col items-center gap-1.5 group"
          aria-label="Chat with Mila"
        >
          <div className="relative">
            <img
              src={MILA_AVATAR}
              alt="Mila"
              className="w-[60px] h-[60px] rounded-full object-cover border-2 border-primary shadow-lg group-hover:scale-105 transition-transform duration-200"
            />
            {hasUnread && (
              <span
                className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-primary rounded-full border-2 border-white"
                style={{ animation: "milaPulse 2s infinite" }}
              />
            )}
          </div>
          <span className="font-head text-[9px] uppercase tracking-[0.25em] bg-[#0A0A0A] text-primary px-2.5 py-1 shadow-lg">
            MILA
          </span>
        </button>
      )}

      {/* ── Chat window ── */}
      {open && (
        <div
          data-testid="mila-chat-window"
          className="fixed bottom-[76px] left-2 right-2 md:bottom-6 md:left-auto md:right-6 z-50 md:w-[360px] flex flex-col shadow-2xl border border-white/10"
          style={{ maxHeight: "min(540px, calc(100vh - 100px))" }}
        >
          {/* Header */}
          <div className="bg-[#0A0A0A] px-4 py-3 flex items-center gap-3 shrink-0">
            <div className="relative shrink-0">
              <img src={MILA_AVATAR} alt="Mila" className="w-10 h-10 rounded-full object-cover border border-white/20" />
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-[#0A0A0A]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-head text-xs uppercase tracking-[0.25em] text-white">Mila</p>
              <p className="text-[11px] text-white/50">In-house Recruiter · Online</p>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="mila-close-btn"
              className="text-white/40 hover:text-white transition-colors p-1"
              aria-label="Close chat"
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto bg-gray-50 px-4 pt-4 pb-2 space-y-0" style={{ minHeight: 0 }}>
            {messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)}
            {typing && <TypingBubble />}

            {/* Quick replies */}
            {isOptions && (
              <div className="flex flex-wrap gap-2 mt-3 mb-2 pl-9">
                {currentNode.options.map((opt) => (
                  <button
                    key={opt.label}
                    onClick={() => handleOption(opt)}
                    data-testid={`mila-option-${opt.label.replace(/\s+/g, "-").toLowerCase()}`}
                    className="font-head text-[10px] uppercase tracking-[0.15em] border border-[#0A0A0A] text-[#0A0A0A] px-3 py-2 hover:bg-primary hover:border-primary transition-colors duration-150 whitespace-nowrap"
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* Done state CTA */}
            {isDone && (
              <div className="flex gap-2 mt-3 mb-2 pl-9 flex-wrap">
                <Link
                  to="/apply"
                  onClick={() => setOpen(false)}
                  className="font-head text-[10px] uppercase tracking-[0.15em] bg-primary text-black px-4 py-2 hover:bg-[#0A0A0A] hover:text-primary transition-colors"
                >
                  Apply Now
                </Link>
                <Link
                  to="/track"
                  onClick={() => setOpen(false)}
                  className="font-head text-[10px] uppercase tracking-[0.15em] border border-[#0A0A0A] px-4 py-2 hover:bg-[#0A0A0A] hover:text-white transition-colors"
                >
                  Track Application
                </Link>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input area */}
          {isInput && (
            <div className="bg-white border-t border-gray-200 px-3 py-2.5 shrink-0">
              {inputError && (
                <p className="text-[10px] text-red-500 font-head uppercase tracking-[0.1em] mb-1.5 px-1">
                  {inputError}
                </p>
              )}
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type={currentNode?.inputKey === "phone" ? "tel" : "text"}
                  value={inputValue}
                  onChange={(e) => { setInputValue(e.target.value); setInputError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && handleInputSubmit()}
                  placeholder={currentNode?.inputPlaceholder || "Type here..."}
                  data-testid="mila-input"
                  className="flex-1 bg-gray-50 border border-gray-200 px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:border-[#0A0A0A] transition-colors"
                />
                <button
                  onClick={handleInputSubmit}
                  disabled={submitting || !inputValue.trim()}
                  data-testid="mila-send-btn"
                  className="w-9 h-9 bg-[#0A0A0A] flex items-center justify-center text-primary hover:bg-primary hover:text-black transition-colors disabled:opacity-40 shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Powered by footer */}
          <div className="bg-[#0A0A0A] px-4 py-1.5 shrink-0">
            <p className="text-[9px] text-white/25 font-head uppercase tracking-[0.2em] text-center">
              Hylas Companion · In-House Recruiting
            </p>
          </div>
        </div>
      )}
    </>
  );
}
